// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
'use client';
/**
 * Le bouton BIO24. Règles et libellés : `shared/docs/UI-CONVENTIONS.md`.
 *
 * L'apparence vient de `styleBouton` (boutonStyles.ts) — jamais redéfinie ici.
 * Ce composant ajoute ce qu'un simple objet de style ne peut pas porter : l'état
 * de chargement, le nom accessible, et les états survol / appui / désactivé.
 */
import React from 'react';
import { Icon } from './Icon';
import { styleBouton, type TailleBouton, type VarianteBouton } from './boutonStyles';

type Natifs = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'type'>;

type Commun = Natifs & {
  variante?: VarianteBouton;
  taille?: TailleBouton;
  /** Désactive le bouton, pose `aria-busy` et remplace le libellé par « … ». */
  chargement?: boolean;
  /** Libellé affiché pendant le chargement, par exemple « Enregistrement… ». Défaut : « … ». */
  libelleChargement?: string;
  /** Nom d'une icône du jeu partagé (`Icon`). */
  icone?: string;
  /** `button` par défaut : un bouton ne soumet jamais un formulaire par accident. */
  type?: 'button' | 'submit' | 'reset';
};

/** Avec un libellé. */
type AvecLibelle = Commun & { children: React.ReactNode };
/** Icône seule : sans texte, le nom accessible est OBLIGATOIRE (« Modifier BIO24-NC-2026-014 »). */
type IconeSeule = Commun & { children?: undefined; icone: string; 'aria-label': string };

export type BoutonProps = AvecLibelle | IconeSeule;

/* États : survol assombri, appui, désactivé. Les `!important` sont voulus : le fond et la
   bordure de base sont en ligne (c'est ce qui les partage avec `btnSmall` & co), et un style
   en ligne l'emporte sur toute règle de feuille. Feuille React 19 `href` + `precedence` :
   dédupliquée et placée dans <head> une seule fois, sans CSS à synchroniser entre les apps. */
const FEUILLE = `
.bio-btn { transition: background var(--dur-fast, 120ms) ease-out, border-color var(--dur-fast, 120ms) ease-out, filter var(--dur-fast, 120ms) ease-out; }
.bio-btn:not(:disabled):active { transform: scale(0.98); }
.bio-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.bio-btn--primaire:not(:disabled):hover { background: var(--cyan-700) !important; }
.bio-btn--secondaire:not(:disabled):hover { background: var(--navy-800) !important; }
.bio-btn--contour:not(:disabled):hover { background: var(--cyan-50) !important; border-color: var(--border-brand) !important; }
.bio-btn--discret:not(:disabled):hover { background: var(--gray-100) !important; }
.bio-btn--danger:not(:disabled):hover { filter: brightness(0.95); }
@media (prefers-reduced-motion: reduce) { .bio-btn { transition: none; } .bio-btn:not(:disabled):active { transform: none; } }
`;

export function Bouton(props: BoutonProps) {
  const {
    variante = 'contour', taille = 'md', chargement = false, libelleChargement, icone,
    type = 'button', className, style, disabled, children, ...reste
  } = props;
  const contenu = chargement ? (libelleChargement ?? '…') : children;
  return (
    <>
      <style href="bio-bouton" precedence="default">{FEUILLE}</style>
      <button
        {...reste}
        type={type}
        disabled={disabled || chargement}
        aria-busy={chargement || undefined}
        className={['bio-btn', `bio-btn--${variante}`, className].filter(Boolean).join(' ')}
        // `style` : marges d'insertion seulement — l'apparence reste celle de la maison.
        style={{ ...styleBouton(variante, taille), ...style }}
      >
        {icone && <Icon name={icone} size={taille === 'sm' ? 14 : 16} />}
        {contenu}
      </button>
    </>
  );
}
